// src/views/admin/users/chefs/view/tabs/LeaveTab.jsx
import React, { useEffect, useState } from 'react'
import Card              from '@/components/ui/cards/Card'
import IsLoadingNotFound from '@/components/ui/tables/IsLoadingNotFound'
import ToastMe           from '@/components/ui/ToastMe'
import { API_BASE_URL }  from '@/constants/ApiConstant'

const API_BASE = API_BASE_URL

/* text + colour helpers --------------------------------------------------- */
const STATUS_TEXT  = { 0: 'Pending', 1: 'Rejected', 2: 'Approved' }
const STATUS_CLASS = {
  0: 'bg-yellow-100 text-yellow-800',   // Pending  → yellow
  1: 'bg-red-100    text-red-800',      // Rejected → red
  2: 'bg-green-100  text-green-800',    // Approved → green
}

export default function LeaveTab({ employeeId }) {
  const [rows, setRows]   = useState([])
  const [loading, setLoad] = useState(true)

  /* bearer header helper with tenant headers */
  const authHdr = () => {
    const tok =
      localStorage.getItem('jwt_token')  ||
      localStorage.getItem('token')      ||
      localStorage.getItem('adminToken') ||
      ''
    const companyId = localStorage.getItem('company_id')
    const branchId = localStorage.getItem('branch_id')
    
    const headers = {}
    
    if (tok) {
      headers['Authorization'] = `Bearer ${tok}`
    }
    if (companyId) {
      headers['X-Company-Id'] = companyId
    }
    if (branchId && branchId !== 'null' && branchId !== '') {
      headers['X-Branch-Id'] = branchId
    }
    
    return headers
  }

  /* fetch once per employeeId */
  useEffect(() => {
    if (!employeeId) {
      setLoad(false)
      return
    }
    
    setLoad(true)
    fetch(`${API_BASE}/leave-request?employee=${employeeId}`, {
      headers: authHdr(),
    })
      .then(r => {
        if (!r.ok) {
          return r.json().then(err => {
            throw new Error(err.message || `Server replied ${r.status}`)
          })
        }
        return r.json()
      })
      .then(json => {
        if (json.success) {
          setRows(Array.isArray(json.data) ? json.data : [])
        } else {
          ToastMe(json.message || 'Failed to load leave requests', 'error')
          setRows([])
        }
      })
      .catch(err => {
        ToastMe(err.message || 'Error loading leave requests', 'error')
        console.error('[LeaveTab] Error:', err)
        setRows([])
      })
      .finally(() => setLoad(false))
  }, [employeeId])

  /* states ---------------------------------------------------------------- */
  if (loading)
    return <Card className="p-6 text-center">Loading leave requests…</Card>

  if (!employeeId)
    return <Card className="p-6 text-center">No employee ID provided.</Card>

  if (!rows.length)
    return (
      <Card className="p-6 text-center text-gray-500">
        No leave requests found for this employee.
      </Card>
    )

  /* table ----------------------------------------------------------------- */
  return (
    <Card className="p-6">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-3 border border-gray-300 font-semibold text-sm">Type</th>
              <th className="p-3 border border-gray-300 font-semibold text-sm">Start Date</th>
              <th className="p-3 border border-gray-300 font-semibold text-sm">End Date</th>
              <th className="p-3 border border-gray-300 font-semibold text-sm text-center">Days</th>
              <th className="p-3 border border-gray-300 font-semibold text-sm">Reason</th>
              <th className="p-3 border border-gray-300 font-semibold text-sm">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(lr => {
              const days = Math.ceil(
                (new Date(lr.endDate) - new Date(lr.startDate)) / 86_400_000 + 1
              )
              return (
                <tr key={lr._id} className="odd:bg-gray-50 hover:bg-gray-100 transition">
                  <td className="p-3 border border-gray-300 capitalize">{lr.leaveType || 'N/A'}</td>
                  <td className="p-3 border border-gray-300">
                    {new Date(lr.startDate).toLocaleDateString()}
                  </td>
                  <td className="p-3 border border-gray-300">
                    {new Date(lr.endDate).toLocaleDateString()}
                  </td>
                  <td className="p-3 border border-gray-300 text-center font-medium">{days}</td>
                  <td className="p-3 border border-gray-300">{lr.reason || '—'}</td>
                  <td className="p-3 border border-gray-300">
                    <span
                      className={
                        `inline-block px-2 py-1 rounded text-xs font-semibold ` +
                        (STATUS_CLASS[lr.status] || 'bg-gray-100 text-gray-800')
                      }
                    >
                      {STATUS_TEXT[lr.status] ?? lr.status}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
